import { v4 as uuid } from 'uuid';
import {
  PipelineRule,
  SdfStepGroupBy,
  SdfStepUpdateValue,
  SuperDuperFiestaDataSource,
} from '../../../data/SuperDuperFiestaData';
import { IRuleFormInput } from '../../../contexts/PipelineRulesContext';

// Parses a string of group bys and returns an array of group bys
// Handles complex group bys by looking for nested parentheses
// It increases the parenCount when it finds an opening parens and decreases it when it finds a closing parens
// and only splits once that count is 0
function parseGroupByString(input: string): string[] {
  const result: string[] = [];
  let current = '';
  let parenCount = 0;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (char === '(') {
      parenCount++;
      current += char;
    } else if (char === ')') {
      parenCount--;
      current += char;
    } else if (char === ',' && parenCount === 0) {
      // Only split on commas that are not inside parentheses
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    result.push(current.trim());
  }

  return result;
}

export function buildRule(
  data: IRuleFormInput,
  selectedRow: PipelineRule | null,
  rows: PipelineRule[],
  firstDataSource: SuperDuperFiestaDataSource | null
): PipelineRule | undefined {
  if (data.entity === null) {
    return undefined;
  }

  const executionOrder = Number(data.executionOrder ? data.executionOrder : rows.length + 1);
  const suvs: SdfStepUpdateValue[] = [];
  const sgbs: SdfStepGroupBy[] = [];

  data.stepUpdateValues?.forEach(suv => {
    if (suv.field !== '' && suv.value !== '') {
      suvs.push(suv);
    }
  });

  if (data.queryGroupBy !== '') {
    const groupBys = parseGroupByString(data.queryGroupBy);

    const newGroupBys = groupBys.map((gb, index) => {
      const existingGroupBy = selectedRow?.stepGroupBys?.find(sgb => sgb.field?.trim() === gb);
      return existingGroupBy ? { ...existingGroupBy, fieldOrder: index + 1 } : { field: gb, fieldOrder: index + 1 };
    });

    // Find and mark removed group bys
    const removedGroupBys =
      selectedRow?.stepGroupBys
        ?.filter(sgb => !groupBys.includes(sgb.field?.trim() || ''))
        .map(sgb => ({ ...sgb, removed: true })) || [];

    sgbs.push(...newGroupBys, ...removedGroupBys);
  }

  return {
    id: executionOrder - 1,
    stepId: selectedRow?.stepId || uuid(),
    name: data.ruleName,
    description: data.description,
    stepTypeName: data.stepType?.displayName || null,
    stepTypeId: data.stepType?.stepTypeId || null,
    entityName: data.entity.displayName,
    entityId: data.entity.entityId,
    dataClassName: data.dataClass?.name,
    dataClassId: data.dataClass?.dataClassId,
    dataSourceName: firstDataSource?.displayName || null,
    dataSourceId: firstDataSource?.dataSourceId || null,
    issueName: data.issue?.name,
    issueId: data.issue?.issueId,
    needsReview: data.needsReview,
    queryCondition: data.queryCondition,
    queryJoin: data.queryJoin,
    queryGroupBy: data.queryGroupBy,
    stepGroupBys: sgbs,
    stepUpdateValues: suvs,
    active: data.active,
    stageId: data.stage?.stageId,
    pipelineId: data.pipeline?.pipelineId,
    executionOrderDisplay: executionOrder,
    executionOrder: executionOrder,
  };
}

export function updateRows(
  newRows: PipelineRule[],
  currentIndex: number | null = null,
  existingRows: PipelineRule[] | null = null
): PipelineRule[] {
  let newOrderedRows = existingRows ? existingRows : [];

  newRows.forEach(r => {
    const tempRows = [...newOrderedRows];

    if (currentIndex != null) {
      // remove row from existing index
      tempRows.splice(currentIndex, 1);
    }
    r.previousStepId = tempRows[Number(r.id) - 1]?.stepId;
    // add row at index
    newOrderedRows = [...tempRows.slice(0, r.executionOrder - 1), r, ...tempRows.slice(r.executionOrder - 1)];
  });

  // update all row id's and order #'s
  newOrderedRows.forEach((row, index) => {
    row.id = index;
    row.executionOrder = index + 1;
    row.executionOrderDisplay = index + 1;
  });

  return newOrderedRows;
}

export function removeFromList(stepId: string, rowList: PipelineRule[]): PipelineRule[] | undefined {
  const rowIndex = rowList.findIndex(r => r.stepId === stepId);
  if (rowIndex === -1) {
    return undefined;
  }

  let tempRows = [...rowList];
  // remove row from existing index
  tempRows.splice(rowIndex, 1);
  // update list
  tempRows.forEach((row, index) => {
    row.id = index;
    row.executionOrder = index + 1;
    row.executionOrderDisplay = index + 1;
  });
  return tempRows;
}

// Removes any rules that have a hierarchy and returns the rules with the updated id, executionOrder, and executionOrderDisplay
// Rules that have a hierarchy are used for tree view
export function transformRules(rules: PipelineRule[]): PipelineRule[] {
  return rules.length === 0
    ? []
    : rules
        .filter(r => r.hierarchy && r.hierarchy.length <= 1)
        .map((row, index) => ({
          ...row,
          id: index,
          executionOrder: index + 1,
          executionOrderDisplay: index + 1,
        }));
}
